import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import React from 'react'
import { Tag } from '@s4l/ds'
import { IconLightning } from '@s4l/icons/IconLightning'
import { useS4LContext } from '@s4l/react-context'
import { PromotionTag as PromotionTagModel } from '@s4l/stayforlong/marketing/PromotionTag'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'

interface PromotionTagProps {
  /**
   * The promotion tag model in JSON
   */
  promoTagJSON?: ReturnType<PromotionTagModel['toJSON']>
  /**
   * If the tag is for a prime user
   */
  prime?: boolean
  /**
   * The discount percentage
   */
  discount: number
  /**
   * Default plain
   */
  variant?: 'rounded' | 'plain'
}

function usePromoTagJSON(
  propPromoTagJSON: ReturnType<PromotionTagModel['toJSON']> | undefined,
  prime?: boolean
) {
  const { data: apiPromo, loading } = useUseCase('GetCurrentPromoMarketingUseCase')

  if (!prime && apiPromo && !apiPromo.isEmpty() && !apiPromo.tag.isEmpty()) {
    return { loading: false, promoJSON: apiPromo.tag.toJSON() }
  }
  return {
    loading,
    promoJSON: propPromoTagJSON,
  }
}

export function PromotionTag({
  discount,
  promoTagJSON,
  prime,
  variant = 'plain',
}: Readonly<PromotionTagProps>) {
  const { i18n } = useS4LContext()
  const { loading, promoJSON: resolvedPromoTagJSON } = usePromoTagJSON(promoTagJSON, prime)

  if (loading) return null

  if (prime) {
    return (
      <Tag
        icon={<IconLightning size={20} />}
        bgColor="var(--color-prime-500)"
        color="var(--color-neutral-100)"
        variant={variant}
        size="m"
      >
        {i18n.formatMessage({ id: 'global.prime' })}
        {i18n.formatMessage({ id: 'discount.today' }, { discount: `: ${discount}%` })}
      </Tag>
    )
  }

  const defaultTag = (
    <Tag
      bgColor="var(--color-primary-500)"
      color="var(--color-neutral-100)"
      variant={variant}
      size="m"
    >
      {`${discount}% `}
      {i18n.formatMessage({ id: 'global.today' })}
    </Tag>
  )

  if (!resolvedPromoTagJSON) return defaultTag

  const tag = PromotionTagModel.fromJSON({ json: resolvedPromoTagJSON })

  if (tag.isEmpty()) {
    return defaultTag
  }

  return (
    <Tag bgColor={tag.background} color={tag.color} variant={variant} size="m">
      {tag.text}
      {` ${discount}%`}
    </Tag>
  )
}

PromotionTag.displayName = 'PromotionTag'
export default withErrorBoundary(PromotionTag)
